/**
 * The bottom-right corner, shared out.
 *
 * Three things dock there and none of them knew about the others: the
 * Assistant's launcher button (and the panel it opens), the find-in-page match
 * bar, and — sitting underneath in normal flow — a transaction list's footer
 * with its rows-per-page controls. Each hard-coded `position: fixed; right:
 * 18-22px; bottom: 18-22px`, so the find bar painted over the Assistant's
 * composer, and the launcher sat on top of the pagination row.
 *
 * The fix is a convention rather than each component knowing about the rest:
 *
 * - **`--twasta-dock-bottom`** — how far up from the bottom edge the corner is
 *   already occupied. A transient floater (the find bar) sets it while it is on
 *   screen; a persistent one (the Assistant) adds it to its own `bottom`, so
 *   the launcher lifts out of the way and drops back when the find bar closes.
 * - **`--twasta-dock-right`** — how much horizontal room a floater takes, so
 *   content that runs to the right edge (the list footer) can reserve it
 *   instead of being covered.
 *
 * Both default to `0px`, so a host that mounts neither floater is unaffected
 * and nothing here has to be conditional.
 */

/** Distance already claimed above the bottom edge. */
export const DOCK_BOTTOM_VAR = '--twasta-dock-bottom'

/** Width already claimed at the right edge. */
export const DOCK_RIGHT_VAR = '--twasta-dock-right'

/** `bottom` for a floater that must clear whatever is already docked. */
export function dockedBottom(base: number): string {
  return `calc(${base}px + var(${DOCK_BOTTOM_VAR}, 0px))`
}

/** Right padding for content that must not run under a floater. */
export function dockedRightPadding(base: number): string {
  return `calc(${base}px + var(${DOCK_RIGHT_VAR}, 0px))`
}

/**
 * Claim space in the corner for as long as the caller is mounted.
 *
 * Reference-counted: two floaters claiming at once must not have the first one
 * to unmount clear the variable out from under the second. Returns the release
 * function, so it drops straight out of a `useEffect`.
 */
export function claimDock(space: { bottom?: number; right?: number }): () => void {
  if (typeof document === 'undefined') return () => {}
  const root = document.body
  const bottom = Math.max(0, space.bottom || 0)
  const right = Math.max(0, space.right || 0)
  claims.push({ bottom, right })
  apply(root)
  return () => {
    const at = claims.findIndex((c) => c.bottom === bottom && c.right === right)
    if (at >= 0) claims.splice(at, 1)
    apply(root)
  }
}

const claims: Array<{ bottom: number; right: number }> = []

function apply(root: HTMLElement): void {
  // The LARGEST claim wins rather than the sum: two floaters stacked in the
  // same corner still only need clearing past the tallest of them.
  const bottom = claims.reduce((most, c) => Math.max(most, c.bottom), 0)
  const right = claims.reduce((most, c) => Math.max(most, c.right), 0)
  root.style.setProperty(DOCK_BOTTOM_VAR, `${bottom}px`)
  root.style.setProperty(DOCK_RIGHT_VAR, `${right}px`)
}
